export const VOICEOVER_URLS = {
  "spectral-manor:lights-out": "/audio/voices/spectral-manor/lights-out.mp3",
  "spectral-manor:hunt-begins": "/audio/voices/spectral-manor/hunt-begins.mp3",
  "spectral-manor:lantern-lost": "/audio/voices/spectral-manor/lantern-lost.mp3",
  "spectral-manor:lantern-relit": "/audio/voices/spectral-manor/lantern-relit.mp3",
  "spectral-manor:hunters-win": "/audio/voices/spectral-manor/hunters-win.mp3",
  "spectral-manor:phantom-win": "/audio/voices/spectral-manor/phantom-win.mp3",
  "family-feud:round-start": "/audio/voices/family-feud/round-start.mp3",
  "family-feud:double-round": "/audio/voices/family-feud/double-round.mp3",
  "family-feud:triple-round": "/audio/voices/family-feud/triple-round.mp3",
  "family-feud:survey-says": "/audio/voices/family-feud/survey-says.mp3",
  "family-feud:strike-one": "/audio/voices/family-feud/strike-one.mp3",
  "family-feud:strike-two": "/audio/voices/family-feud/strike-two.mp3",
  "family-feud:three-strikes": "/audio/voices/family-feud/three-strikes.mp3",
  "family-feud:round-awarded": "/audio/voices/family-feud/round-awarded.mp3",
  "family-feud:winner": "/audio/voices/family-feud/winner.mp3",
  "puzzle-fighter:ready-fight": "/audio/voices/puzzle-fighter/ready-fight.mp3",
  "puzzle-fighter:two-chain": "/audio/voices/puzzle-fighter/two-chain.mp3",
  "puzzle-fighter:three-chain": "/audio/voices/puzzle-fighter/three-chain.mp3",
  "puzzle-fighter:monster-chain": "/audio/voices/puzzle-fighter/monster-chain.mp3",
  "puzzle-fighter:counter-attack": "/audio/voices/puzzle-fighter/counter-attack.mp3",
  "puzzle-fighter:danger": "/audio/voices/puzzle-fighter/danger.mp3",
  "puzzle-fighter:ko": "/audio/voices/puzzle-fighter/ko.mp3",
  "king-of-tokyo:battle-begins": "/audio/voices/king-of-tokyo/battle-begins.mp3",
  "king-of-tokyo:tokyo-claimed": "/audio/voices/king-of-tokyo/tokyo-claimed.mp3",
  "king-of-tokyo:yield-or-fight": "/audio/voices/king-of-tokyo/yield-or-fight.mp3",
  "king-of-tokyo:monster-fallen": "/audio/voices/king-of-tokyo/monster-fallen.mp3",
  "king-of-tokyo:massive-smash": "/audio/voices/king-of-tokyo/massive-smash.mp3",
  "king-of-tokyo:victory-points": "/audio/voices/king-of-tokyo/victory-points.mp3",
  "king-of-tokyo:last-monster": "/audio/voices/king-of-tokyo/last-monster.mp3",
  "king-of-tokyo:tokyo-survives": "/audio/voices/king-of-tokyo/tokyo-survives.mp3",
} as const;

export type VoiceoverCue = keyof typeof VOICEOVER_URLS;
export type VoiceoverPack = "spectral-manor" | "family-feud" | "puzzle-fighter" | "king-of-tokyo";

export const voicesEnabledFromPreference = (value: string | null) => value !== "off";

export const VOICEOVER_PACKS: Record<VoiceoverPack, VoiceoverCue[]> = {
  "spectral-manor": Object.keys(VOICEOVER_URLS).filter((cue) => cue.startsWith("spectral-manor:")) as VoiceoverCue[],
  "family-feud": Object.keys(VOICEOVER_URLS).filter((cue) => cue.startsWith("family-feud:")) as VoiceoverCue[],
  "puzzle-fighter": Object.keys(VOICEOVER_URLS).filter((cue) => cue.startsWith("puzzle-fighter:")) as VoiceoverCue[],
  "king-of-tokyo": Object.keys(VOICEOVER_URLS).filter((cue) => cue.startsWith("king-of-tokyo:")) as VoiceoverCue[],
};

type FeudSnapshot = {
  roundNumber: number;
  answers: Array<{ revealed: boolean }>;
  strikes: number;
  multiplier: number;
  roundAwardedTo: string | null;
  winnerTeam: string | null;
};

type PuzzleSnapshot = {
  moveCount: number;
  winnerId: string | null;
  lastMove: { chain: number; attack: number } | null;
  players: Record<string, { board: Array<unknown | null> } | null>;
};

type TokyoSnapshot = {
  players: Record<string, { alive: boolean }>;
  tokyoCityId: string | null;
  tokyoBayId: string | null;
  winnerIds: string[];
  winReason: "victory_points" | "last_monster" | "mutual_destruction" | "final_roar" | null;
  lastRollResult: { id: string; clawsRolled: number; attackDamage: number } | null;
};

export type VoiceRoomSnapshot = {
  gameType: string;
  phase: string;
  familyFeud?: FeudSnapshot;
  puzzleFighter?: PuzzleSnapshot;
  kingTokyo?: TokyoSnapshot;
};

function puzzleDanger(view: PuzzleSnapshot | undefined) {
  if (!view) return false;
  return Object.values(view.players).some((player) => player?.board.slice(0, 12).some(Boolean));
}

function familyFeudCue(previous: VoiceRoomSnapshot, current: VoiceRoomSnapshot): VoiceoverCue | null {
  const before = previous.familyFeud;
  const after = current.familyFeud;
  if (!before || !after) return null;
  if (previous.phase !== "finished" && current.phase === "finished") return "family-feud:winner";
  if (!before.roundAwardedTo && after.roundAwardedTo) return "family-feud:round-awarded";
  if (after.strikes > before.strikes) {
    if (after.strikes >= 3) return "family-feud:three-strikes";
    return after.strikes === 2 ? "family-feud:strike-two" : "family-feud:strike-one";
  }
  if (after.answers.filter((answer) => answer.revealed).length > before.answers.filter((answer) => answer.revealed).length) return "family-feud:survey-says";
  if (after.roundNumber > before.roundNumber) {
    if (after.multiplier >= 3) return "family-feud:triple-round";
    if (after.multiplier === 2) return "family-feud:double-round";
    return "family-feud:round-start";
  }
  return null;
}

function puzzleFighterCue(previous: VoiceRoomSnapshot, current: VoiceRoomSnapshot): VoiceoverCue | null {
  const before = previous.puzzleFighter;
  const after = current.puzzleFighter;
  if (!before || !after) return null;
  if (previous.phase === "lobby" && current.phase === "puzzle_fighter_playing") return "puzzle-fighter:ready-fight";
  if (previous.phase !== "finished" && current.phase === "finished" && after.winnerId) return "puzzle-fighter:ko";
  if (after.moveCount > before.moveCount && after.lastMove) {
    if (after.lastMove.chain >= 4) return "puzzle-fighter:monster-chain";
    if (after.lastMove.chain === 3) return "puzzle-fighter:three-chain";
    if (after.lastMove.chain === 2) return "puzzle-fighter:two-chain";
    if (after.lastMove.attack >= 4) return "puzzle-fighter:counter-attack";
  }
  if (!puzzleDanger(before) && puzzleDanger(after)) return "puzzle-fighter:danger";
  return null;
}

function kingTokyoCue(previous: VoiceRoomSnapshot, current: VoiceRoomSnapshot): VoiceoverCue | null {
  const before = previous.kingTokyo;
  const after = current.kingTokyo;
  if (!before || !after) return null;
  if (previous.phase === "lobby" && current.phase === "tokyo_roll") return "king-of-tokyo:battle-begins";
  if (previous.phase !== "finished" && current.phase === "finished") {
    if (after.winReason === "victory_points") return "king-of-tokyo:victory-points";
    if (after.winReason === "last_monster" || after.winReason === "final_roar") return "king-of-tokyo:last-monster";
    return "king-of-tokyo:tokyo-survives";
  }
  if (Object.keys(after.players).some((id) => before.players[id]?.alive && !after.players[id]?.alive)) return "king-of-tokyo:monster-fallen";
  const beforeOccupants = [before.tokyoCityId, before.tokyoBayId].filter(Boolean);
  const afterOccupants = [after.tokyoCityId, after.tokyoBayId].filter(Boolean);
  if (afterOccupants.some((id) => !beforeOccupants.includes(id))) return "king-of-tokyo:tokyo-claimed";
  if (previous.phase !== "tokyo_yield" && current.phase === "tokyo_yield") return "king-of-tokyo:yield-or-fight";
  if (after.lastRollResult?.id !== before.lastRollResult?.id && (after.lastRollResult?.clawsRolled || 0) >= 3 && (after.lastRollResult?.attackDamage || 0) >= 3) return "king-of-tokyo:massive-smash";
  return null;
}

export function voiceoverCueForTransition(previous: VoiceRoomSnapshot | null, current: VoiceRoomSnapshot): VoiceoverCue | null {
  if (!previous || previous.gameType !== current.gameType) return null;
  if (current.gameType === "family_feud") return familyFeudCue(previous, current);
  if (current.gameType === "puzzle_fighter") return puzzleFighterCue(previous, current);
  if (current.gameType === "king_of_tokyo") return kingTokyoCue(previous, current);
  return null;
}

export function voiceoverPackForGame(gameType: string): VoiceoverPack | null {
  if (gameType === "family_feud") return "family-feud";
  if (gameType === "puzzle_fighter") return "puzzle-fighter";
  if (gameType === "king_of_tokyo") return "king-of-tokyo";
  return null;
}
